import { useEffect, useState } from "react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useProductData } from "@/zustand/productStore.js";
import glideImage from "@/func/glideImage";
import { videoSrc, thumbnailSrc } from "../../utils/media";

const SwiperGallery = () => {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1071);
  const productData = useProductData();
  const heroContent = window.post_template.checkout_v4_product_hero ?? {};
  const useNewGallery = heroContent.checkout_v4_product_hero_image_toggle;
  // ACF returns `false` for an empty gallery, and `?hero=1` can force the hero on for a checkout
  // that never filled one in — fall back to an empty slider instead of throwing.
  const images = useNewGallery
    ? (heroContent.checkout_v4_product_hero_image_slider_gallery_new || []).map((item, index) => {
        let thumbDisplayUrl;

        if (item.use_video && item.asset_type === "migrate") {
          thumbDisplayUrl = thumbnailSrc(item.video_id);
        } else {
          const source = item.use_video ? item.video_thumb || item.gallery_image : item.gallery_image;
          thumbDisplayUrl = glideImage(source, { w: 150 });
        }

        return {
          original: glideImage(item.gallery_image, { w: 500 }),
          mobile: glideImage(item.gallery_image, { w: 420 }),
          video_id: item.video_id,
          video_url: item.video_url,
          asset_type: item.asset_type,
          index,
          thumbnail: item.video_thumb || glideImage(item.gallery_image, { w: 150 }),
          thumbDisplayUrl: thumbDisplayUrl,
          useVideo: item.use_video,
          autoplay: item.video_autofplay,
          loop: item.video_loop,
        };
      })
    : (heroContent.checkout_v4_product_hero_image_slider_gallery || []).map((desktopItem) => {
        const thumbUrl = glideImage(desktopItem.url, { w: 150 });
        return {
          original: glideImage(desktopItem.url, { w: 500 }),
          thumbnail: thumbUrl,
          thumbDisplayUrl: thumbUrl,
          mobile: glideImage(desktopItem.url, { w: 420 }),
        };
      });

  const handleVideoPointerEvents = () => {
    const videoIframes = document.querySelectorAll(".hero-gallery__video iframe");
    videoIframes.forEach((iframe) => {
      iframe.style.pointerEvents = "none";

      iframe.addEventListener("mouseenter", function () {
        iframe.style.pointerEvents = "auto";
      });

      iframe.addEventListener("mouseleave", function () {
        iframe.style.pointerEvents = "none";
      });

      let touchStartX = 0;
      let touchStartY = 0;

      iframe.addEventListener(
        "touchstart",
        function (e) {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;

          if (e.touches.length === 1) {
            iframe.style.pointerEvents = "auto";
            setTimeout(() => {
              iframe.style.pointerEvents = "none";
            }, 800);
          }
        },
        { passive: true }
      );

      iframe.addEventListener(
        "touchmove",
        function (e) {
          const touchMoveX = e.touches[0].clientX;
          const touchMoveY = e.touches[0].clientY;

          const deltaX = Math.abs(touchMoveX - touchStartX);
          const deltaY = Math.abs(touchMoveY - touchStartY);

          if (deltaX > 10 || deltaY > 10) {
            iframe.style.pointerEvents = "none";
          }
        },
        { passive: true }
      );
    });
  };

  const handleSlideChange = (e) => {
    const nextSlide = e.realIndex;
    handleVideoPlay(nextSlide);
  };

  const handleVideoPlay = (index) => {
    const player = document.getElementById(`video_player-${index}`);
    if (!player) return;

    if (player instanceof HTMLVideoElement) {
      const source = player.querySelector("source");
      if (source?.dataset.src) {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
        player.load();
        player.play().catch((err) => {
          if (err.name === "AbortError") return;
          throw err;
        });
      }
      return;
    }

    if (player instanceof HTMLIFrameElement && player.dataset.src) {
      player.src = player.dataset.src;
      player.removeAttribute("data-src");
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1071);
    };

    handleVideoPlay(0);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setTimeout(handleVideoPointerEvents, 500);
  }, []);

  useEffect(() => {
    if (thumbsSwiper) {
      thumbsSwiper.on("slideChange", handleVideoPointerEvents);
    }

    return () => {
      if (thumbsSwiper) {
        thumbsSwiper.off("slideChange", handleVideoPointerEvents);
      }
    };
  }, [thumbsSwiper]);

  const NextButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} style={{ transform: "rotate(180deg)" }} fill="none">
      <path
        stroke="#333"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.667 9.333 12 16l6.667 6.667"
      />
    </svg>
  );

  const PrevButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width={32} height={32} fill="none">
      <path
        stroke="#333"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18.667 9.333 12 16l6.667 6.667"
      />
    </svg>
  );

  const cdnBase = (() => {
    const hostname = window.location.hostname;
    if (hostname === "localhost") return "https://cdn.getmytestdomain.com";
    if (hostname.includes(".")) return `https://cdn.${hostname}`;
    return `https://cdn.${hostname}.com`;
  })();

  return (
    <div
      className={`hero-swiper-container ${
        productData?.show_product_picker_v2 && "hero-swiper-container__productPickerV2"
      }`}
    >
      <Swiper
        style={{
          width: isMobile ? "100%" : "calc(100% - 90px)",
          height: "100%",
          borderRadius: "10px",
          marginLeft: "0",
          paddingBottom: "0",
          order: isMobile ? 1 : 2,
        }}
        onSlideChange={handleSlideChange}
        onSwiper={handleVideoPointerEvents}
        loop={false}
        spaceBetween={10}
        navigation={{
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        }}
        thumbs={{ swiper: thumbsSwiper }}
        modules={[FreeMode, Navigation, Thumbs]}
        className="main-swiper"
      >
        {images.map((item, index) => {
          return (
            <SwiperSlide key={`main-${index}`}>
              {item.useVideo ? (
                item.asset_type === "iframe" ? (
                  <IframeVideo item={item} index={index} />
                ) : item.asset_type === "migrate" ? (
                  <Video
                    src={videoSrc(`/streamsMigration/videos/${item.video_id}`)}
                    thumbnail={thumbnailSrc(item.video_id)}
                    index={index}
                  />
                ) : (
                  <Video
                    src={videoSrc(item.video_url)}
                    type={item.asset_type}
                    thumbnail={item.thumbnail}
                    index={index}
                  />
                )
              ) : (
                <picture>
                  <source srcset={item.original} media="(min-width: 678px)" />
                  <source srcset={item.mobile} media="(max-width: 677px)" />
                  <img
                    src={item.original}
                    alt={`Slide ${index + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "fill" }}
                    loading={index === 0 ? "eager" : "lazy"}
                    className="mon-img-500-w"
                  />
                </picture>
              )}
            </SwiperSlide>
          );
        })}
        <div className="swiper-button-prev">
          <PrevButton />
        </div>
        <div className="swiper-button-next">
          <NextButton />
        </div>
      </Swiper>

      {/*Thumbnail Swiper*/}
      <Swiper
        onSwiper={setThumbsSwiper}
        spaceBetween={10}
        direction={isMobile ? "horizontal" : "vertical"}
        style={{
          width: isMobile ? "100%" : "80px",
          height: "100%",
          marginLeft: "0",
          marginRight: isMobile ? "0" : "10px",
          marginTop: isMobile ? "10px" : "0",
          paddingBottom: "0",
          order: isMobile ? 2 : 1,
        }}
        loop={false}
        breakpoints={{
          0: {
            slidesPerView: 5,
          },
          540: {
            slidesPerView: 7,
          },
          767: {
            slidesPerView: 5,
          },
        }}
        slidesPerView={5}
        freeMode={true}
        watchSlidesProgress={true}
        modules={[FreeMode, Navigation, Thumbs]}
        className="thumbs-swiper"
      >
        {images.map((img, index) => (
          <SwiperSlide key={`thumb-${index}`} style={{ overflow: "hidden", borderRadius: "4px" }}>
            <img
              src={img.thumbDisplayUrl}
              alt={`Thumbnail ${index + 1}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              className="mon-img-150-w"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const Video = ({ src, thumbnail, index, type }) => {
  return (
    <div>
      <video
        id={`video_player-${index}`}
        data-type={type}
        style={{ width: "100%", height: "100%" }}
        muted
        loop
        autoPlay
        playsInline
        poster={index === 0 ? glideImage(thumbnail, { w: 500 }) : thumbnail}
      >
        {index === 0 ? <source src={src} type="video/mp4" /> : <source data-src={src} type="video/mp4" />}
      </video>
    </div>
  );
};

const IframeVideo = ({ item, index }) => {
  return (
    <div className="hero-gallery__videoWrapper">
      <div className="hero-gallery__video">
        <iframe
          id={`video_player-${index}`}
          className="content__image-video"
          data-src={
            index !== 0
              ? `https://iframe.videodelivery.net/${item.video_id}/iframe/?controls=${!item.autoplay}&autoplay=${
                  item.autoplay
                }&loop=${item.loop}&muted=${item.autoplay}`
              : ""
          }
          src={
            index === 0
              ? `https://iframe.videodelivery.net/${item.video_id}/iframe/?controls=${!item.autoplay}&autoplay=${
                  item.autoplay
                }&loop=${item.loop}&muted=${item.autoplay}`
              : ""
          }
          style={{
            position: "absolute",
            height: "100%",
            width: "100%",
            top: "50%",
            left: "50%",
            transform: "scale(1.01) translate(-50%, -50%)",
            borderRadius: "10px",
            touchAction: "pan-y",
          }}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen={true}
        ></iframe>
      </div>
    </div>
  );
};

export default SwiperGallery;
